import { useState, useEffect } from 'react';

const SEARCH_HISTORY_KEY = 'search_history';
const MAX_HISTORY_ITEMS = 10;

export const useSearchHistory = () => {
  const [searchHistory, setSearchHistory] = useState([]);

  // Load search history from localStorage on mount
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem(SEARCH_HISTORY_KEY);
      if (savedHistory) {
        const parsed = JSON.parse(savedHistory);
        if (Array.isArray(parsed)) {
          setSearchHistory(parsed);
        }
      }
    } catch (error) {
      console.error('Error loading search history:', error);
    }
  }, []);

  // Add a search term to history
  const addToHistory = (searchTerm) => {
    if (!searchTerm || typeof searchTerm !== 'string' || searchTerm.trim().length === 0) {
      return;
    }

    const trimmedTerm = searchTerm.trim().toLowerCase();
    
    setSearchHistory(prevHistory => {
      // Remove if already exists (to avoid duplicates)
      const filteredHistory = prevHistory.filter(
        item => item.query.toLowerCase() !== trimmedTerm
      );
      
      // Create new history entry
      const newEntry = {
        id: Date.now(),
        query: searchTerm.trim(),
        timestamp: new Date().toISOString(),
        count: 1
      };
      
      // Add to beginning and limit to MAX_HISTORY_ITEMS
      const newHistory = [newEntry, ...filteredHistory].slice(0, MAX_HISTORY_ITEMS);
      
      // Save to localStorage
      try {
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
      } catch (error) {
        console.error('Error saving search history:', error);
      }
      
      return newHistory;
    });
  };

  // Clear all search history
  const clearHistory = () => {
    setSearchHistory([]);
    try {
      localStorage.removeItem(SEARCH_HISTORY_KEY);
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  };

  // Remove specific item from history
  const removeFromHistory = (id) => {
    setSearchHistory(prevHistory => {
      const newHistory = prevHistory.filter(item => item.id !== id);
      
      try {
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
      } catch (error) {
        console.error('Error updating search history:', error);
      }
      
      return newHistory;
    });
  };

  // Get formatted time for display
  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const searchTime = new Date(timestamp);
    const diffMs = now - searchTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return searchTime.toLocaleDateString();
  };

  return {
    searchHistory,
    addToHistory,
    clearHistory,
    removeFromHistory,
    getTimeAgo,
    hasHistory: searchHistory.length > 0
  };
};